import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

/**
 * Searchable SKU picker.
 * Props:
 *  - skus: [{ id, sku_name, sku_code? }]
 *  - value: string (selected sku id)
 *  - onChange: (id) => void
 *  - placeholder: string
 *  - testid: string (root button data-testid)
 *  - disabled?: bool
 *  - getLabel?: (sku) => string  // override displayed label (defaults to sku_name)
 */
export default function SkuCombobox({
  skus = [],
  value = "",
  onChange,
  placeholder = "Select SKU",
  testid,
  disabled = false,
  getLabel,
}) {
  const [open, setOpen] = useState(false);
  const selected = skus.find((s) => s.id === value);
  const label = (s) => (getLabel ? getLabel(s) : s.sku_name);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal h-10"
          data-testid={testid}
        >
          <span className={selected ? "" : "text-muted-foreground"}>
            {selected ? label(selected) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[70vh]"
        align="start"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput
            placeholder="Search products..."
            data-testid={testid ? `${testid}-search` : undefined}
          />
          <CommandList
            className="max-h-[60vh] overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <CommandEmpty>No products match.</CommandEmpty>
            <CommandGroup>
              {skus.map((s) => (
                <CommandItem
                  key={s.id}
                  // We use the sku_name (+ optional code) as the searchable string
                  // so the cmdk filter matches user-typed text against both.
                  value={`${s.sku_name} ${s.sku_code || ""}`}
                  onSelect={() => {
                    onChange?.(s.id);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-item-${s.id}` : undefined}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${s.id === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span>{label(s)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
